import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import type { Role, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { describeRejection } from "@/modules/settings/errors";

/**
 * "Invite user" (FR-ACL-2, M10-7).
 *
 * A `Sheet` rather than a new panel type: it is the same primitive
 * `RecordDrawer` already opens over the list, and reusing it means this form
 * gets the focus trap, the Escape handler and the mobile-width behaviour for
 * free instead of a second implementation of all three.
 *
 * The account is created the moment this submits — there is no draft state
 * on the server side to discard — so "Cancel" only resets what is typed
 * here, never anything already sent.
 */
export function InviteUserSheet({
  open,
  onOpenChange,
  roles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  function reset() {
    setName("");
    setEmail("");
    setRoleIds([]);
    setRejection(null);
  }

  const inviteMutation = useMutation({
    mutationFn: () =>
      data.settings.inviteUser({
        name: name.trim(),
        email: email.trim(),
        roleIds,
      }),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      reset();
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["settings", "users"] });
      if (!result.data.emailSent) {
        // An expected failure, not a 500 (CLAUDE.md) — the account exists
        // and "resend" on its row is the recovery, not this dialog again.
        window.alert(
          `${result.data.user.name} was invited, but the email could not be sent. Use "Resend invite" on their row to try again.`,
        );
      }
    },
  });

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title="Invite user"
        description="They receive an email with a link to set their password."
        actions={<FeedbackButton />}
      >
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            setRejection(null);
            inviteMutation.mutate();
          }}
        >
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                required
              />
              {fieldError("name") ? (
                <p className="text-destructive text-xs">{fieldError("name")}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                required
              />
              {fieldError("email") ? (
                <p className="text-destructive text-xs">
                  {fieldError("email")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Role</Label>
              <div className="space-y-2 rounded-md border p-3">
                {roles.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No roles exist yet.
                  </p>
                ) : (
                  roles.map((role) => (
                    <div key={role.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`invite-role-${role.id}`}
                        checked={roleIds.includes(role.id)}
                        onCheckedChange={(checked) => {
                          setRoleIds((prev) =>
                            checked
                              ? [...prev, role.id]
                              : prev.filter((id) => id !== role.id),
                          );
                        }}
                      />
                      <Label
                        htmlFor={`invite-role-${role.id}`}
                        className="text-sm font-normal"
                      >
                        {role.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {fieldError("roleIds") ? (
                <p className="text-destructive text-xs">
                  {fieldError("roleIds")}
                </p>
              ) : null}
            </div>

            {rejection && rejection.kind !== "invalid" ? (
              <p className="text-destructive text-sm">
                {describeRejection(rejection)}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                inviteMutation.isPending ||
                name.trim() === "" ||
                email.trim() === ""
              }
            >
              {inviteMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Send invite
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
